import { env } from '@repo/env';

type BannerAsset = {
  path: string;
  trim: string;
  dimensions: {
    mobile: {
      height: number;
      width: number;
    };
    desktop: {
      height: number;
      width: number;
    };
  };
};

const DIR = 'cdn/banking/banking-assessment-banner-images';

// `trim` crops each imagedown to its artwork before resizing. Without it, objectFit renders the art
// off-center and small. Values are `top;right;bottom;left` in source pixels.
const ASSETS = {
  savingsCompare: {
    dimensions: {
      mobile: {
        height: 159,
        width: 202,
      },
      desktop: {
        height: 97,
        width: 123,
      },
    },
    path: `${DIR}/full-savings-compare-banner-icon.png`,
    trim: '244;272;432;186',
  },
  cd: {
    dimensions: {
      desktop: {
        height: 85,
        width: 85,
      },
      mobile: {
        height: 159,
        width: 159,
      },
    },
    path: `${DIR}/full-cd-banner-icon.png`,
    trim: '244;751;197;751',
  },
  cdCompare: {
    dimensions: {
      desktop: {
        height: 97,
        width: 123,
      },
      mobile: {
        height: 159,
        width: 202,
      },
    },
    path: `${DIR}/full-cd-compare-banner-icon.png`,
    trim: '246;272;432;199',
  },
  checking: {
    dimensions: {
      desktop: {
        height: 97,
        width: 97,
      },
      mobile: {
        height: 159,
        width: 159,
      },
    },
    path: `${DIR}/full-checking-banner-icon.png`,
    trim: '111;914;89;206',
  },
  checkingCompare: {
    dimensions: {
      desktop: {
        height: 97,
        width: 123,
      },
      mobile: {
        height: 159,
        width: 201,
      },
    },
    path: `${DIR}/full-checking-compare-banner-icon.png`,
    trim: '245;273;432;221',
  },
} satisfies Record<string, BannerAsset>;

const IMAGE_ASSETS = {
  'high-yield-online-savings-accounts': { a: ASSETS.savingsCompare, b: ASSETS.savingsCompare },
  'cd-rates': { a: ASSETS.cd, b: ASSETS.cdCompare },
  'checking-accounts': { a: ASSETS.checking, b: ASSETS.checkingCompare },
  'free-checking-accounts': { a: ASSETS.checking, b: ASSETS.checkingCompare },
  'online-checking-accounts': { a: ASSETS.checking, b: ASSETS.checkingCompare },
};

// Shared Cloudflare options:
// - format=auto: serve AVIF/WebP where supported (smaller at equal quality)
// - fit=scale-down: never enlarge past the source (no wasted bytes today; crisp once source art is hi-res)
// - quality=85 / sharpen=1: keep icons crisp when downscaled
const BASE_OPTS = 'format=auto,fit=scale-down,quality=85,sharpen=1';

function cf(asset: BannerAsset, extra: string) {
  // trim runs before resize, so the artwork fills the frame and centers cleanly
  return `${env.NEXT_PUBLIC_NW_URL}/cdn-cgi/image/trim=${asset.trim},${extra},${BASE_OPTS}/${asset.path}`;
}

/**
 * Widths correspond to the mobile display box (252px) at 1x/2x/3x so high-DPR
 * phones pull a properly sized image. The browser picks via the `sizes` attr.
 */
const SRCSET_WIDTHS = [252, 504, 756];

export type BannerImage = {
  src: string;
  srcSet: string;
  sizes: string;
  dimensions: {
    mobile: {
      height: number;
      width: number;
    };
    desktop: {
      height: number;
      width: number;
    };
  };
};

function buildBannerImage(asset: BannerAsset): BannerImage {
  const { mobile, desktop } = asset.dimensions;
  return {
    src: cf(asset, 'width=252'),
    srcSet: SRCSET_WIDTHS.map(w => `${cf(asset, `width=${w}`)} ${w}w`).join(', '),
    // Match the rendered width per breakpoint (md = 1080px) so the browser picks
    // the right srcSet candidate. Derived from this asset's own dimensions.
    sizes: `(max-width: 1080px) ${mobile.width}px, ${desktop.width}px`,
    dimensions: asset.dimensions,
  };
}

export function getImagesForBanner(slug: string) {
  if (!Object.hasOwn(IMAGE_ASSETS, slug)) {
    return undefined;
  }
  const assets = IMAGE_ASSETS[slug as keyof typeof IMAGE_ASSETS];
  return { a: buildBannerImage(assets.a), b: buildBannerImage(assets.b) };
}
